import { CloseRounded } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, Typography, useTheme } from '@mui/material'
import Dropzone, { ILayoutProps } from 'react-dropzone-uploader'
import TabPanel from '../../../../shared/components/TabPanel'
import { CancelButton, SaveButton } from '../../../../shared/components/inputs'
import 'react-dropzone-uploader/dist/styles.css'
import { useTranslation } from 'react-i18next'
import TinyRichEditor from '../../../../shared/components/TinyRichEditor'
import { formsStyles } from '../../../../shared/styles/FormsStyles'
import { useLessonEditor } from '../hooks/useLessonEditor'
import { StyledDialog } from '@/contexts/shared/styles/StyledDialog'
import useTabState from '@/contexts/shared/hooks/useTabState'
import CustomTextField from '@/contexts/shared/components/inputs/CustomTextField'
import { StyledTabs } from '@/contexts/shared/components/StyledTabs'
import { CustomTab } from '@/contexts/backoffice/users/ui/components/modals/create/CustomTab'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import NotificationGroup from './NotificationGroup'

type LessonEditorProps = {
  courseId: number
  courseTitle: string
  courseLastOrder: number
}

const Layout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }: ILayoutProps) => {
  return (
    <div>
      {previews}
      <div {...dropzoneProps}>{files.length < maxFiles && input}</div>
      {files.length > 0 && submitButton}
    </div>
  )
}

function LessonModal({ courseId, courseTitle, courseLastOrder }: LessonEditorProps) {
  const { t } = useTranslation(['global'])
  const theme = useTheme()
  const formClasses = formsStyles(theme)
  const { currentTab, setCurrentTab, a11yProps } = useTabState()

  const {
    lessonState,
    confirm,
    showForm,
    isValidForm,
    errorForm,
    errorTab,
    msgForm,
    handleInputChange,
    handleNotificationsChange,
    handleSubmit,
    handleConfirmSubmit,
    getUploadParams,
    handleCloseForm
  } = useLessonEditor({ courseId, courseLastOrder })

  return (
    <>
      <StyledDialog
        open={showForm}
        onClose={handleCloseForm}
        aria-labelledby="lesson-dialog-title"
        aria-describedby="lesson-dialog-desc"
        disableEnforceFocus
      >
        <DialogTitle id="lesson-dialog-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">
              {lessonState.id !== null
                ? `${t('edit')} ${t('lesson')} del curso: ${courseTitle}`
                : `${t('create')} ${t('lesson')} del curso: ${courseTitle}`}
            </Typography>
            <Button onClick={handleCloseForm} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Grid item xs={6} md={6}>
              <CustomTextField inputName="title" value={lessonState.title} handleInputChange={handleInputChange} required />
            </Grid>
            <Grid item xs={6} md={3}>
              <CustomTextField inputName="duration" value={lessonState.duration} handleInputChange={handleInputChange} required />
            </Grid>
            <Grid item xs={6} md={3}>
              <CustomTextField inputName="order" value={lessonState.order} handleInputChange={handleInputChange} required />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <StyledTabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} aria-label="lesson tabs">
              <CustomTab label={t('general')} {...a11yProps(0)} error={errorTab.general} />
              <CustomTab label={t('downloadable-material')} {...a11yProps(1)} />
            </StyledTabs>
          </Grid>
          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TinyRichEditor handleInputChange={handleInputChange} value={lessonState.content} uploadURL="/lessons/upload" />
                <FormHelperText error={errorForm.content}>{msgForm.content}</FormHelperText>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <NotificationGroup lessonState={lessonState} handleNotificationsChange={handleNotificationsChange} />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Dropzone
                  getUploadParams={getUploadParams}
                  LayoutComponent={Layout}
                  classNames={{ dropzone: 'dzu-dropzone-custom', inputLabel: 'dzu-inputlabel-custom' }}
                  inputContent={t('dropzone-drop-text')}
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={!isValidForm && Object.keys(msgForm).length > 0 ? formClasses.errorDialogActions : {}}>
          <CancelButton onClick={handleCloseForm}>{lessonState.id !== null ? t('cancel') : t('cancel')}</CancelButton>
          <SaveButton data-createlesson="saveButton" onClick={handleSubmit}>
            {lessonState.id !== null ? t('save') : t('create')}
          </SaveButton>
        </DialogActions>
      </StyledDialog>

      <ConfirmDeleteModal
        confirm={confirm}
        lessonState={lessonState}
        handleCloseForm={handleCloseForm}
        handleConfirmSubmit={handleConfirmSubmit}
      />
    </>
  )
}

export default LessonModal
